/**
 * VerificationAuditLogTab — Super-admin only, read-only view over the
 * immutable `verification_audit_log` collection. iter209.
 *
 * Records are written server-side whenever an admin flips a user's
 * `verified` flag. There is NO endpoint to edit or delete entries,
 * matching the acceptance requirement: "السجلات لا تكون قابلة للتعديل
 * أو الحذف من واجهة الإدارة." Archive toggle is a read-side view
 * concern — hidden by default, revealed with the switch, never
 * destructive.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, RefreshCw, Search, Loader2, Filter, User } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

function fmt(iso, lang) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB"); }
  catch { return String(iso).slice(0, 19); }
}

export default function VerificationAuditLogTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [fromAt, setFromAt] = useState("");
  const [toAt, setToAt] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const params = useMemo(() => {
    const p = { include_archived: includeArchived, limit: 300 };
    if (q.trim()) p.q = q.trim();
    if (kind !== "all") p.kind = kind;
    if (fromAt) p.from_at = new Date(fromAt).toISOString();
    if (toAt) p.to_at = new Date(toAt).toISOString();
    return p;
  }, [q, kind, fromAt, toAt, includeArchived]);

  const reload = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await apiClient.get("/admin/verification-audit-log", { params });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }, [params]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-5" data-testid="verif-audit-log-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "سجل توثيق الحسابات" : "Account verification audit log"}
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar"
              ? "سجل ثابت لجميع عمليات التوثيق وإلغاء التوثيق. لا يمكن تعديله أو حذفه."
              : "Immutable record of every verify / unverify action. Cannot be edited or deleted."}
          </p>
        </div>
        <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="audit-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div className="surface rounded-2xl p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <label className="text-[10px] uppercase text-[#8a8a8a] block mb-1 inline-flex items-center gap-1">
            <Search className="w-3 h-3" /> {lang === "ar" ? "بحث بالاسم / البريد" : "Search name / email"}
          </label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "ar" ? "اسم المسؤول أو الحساب…" : "Admin or account…"}
            className="input-dark h-9 text-sm"
            data-testid="audit-search"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase text-[#8a8a8a] block mb-1 inline-flex items-center gap-1">
            <Filter className="w-3 h-3" /> {lang === "ar" ? "الحالة" : "Action"}
          </label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  className="input-dark h-9 text-sm" data-testid="audit-kind">
            <option value="all">{lang === "ar" ? "الكل" : "All"}</option>
            <option value="verified">{lang === "ar" ? "توثيق" : "Verified"}</option>
            <option value="unverified">{lang === "ar" ? "إلغاء توثيق" : "Unverified"}</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[#8a8a8a] block mb-1">
            {lang === "ar" ? "من تاريخ" : "From"}
          </label>
          <input type="date" value={fromAt} onChange={(e) => setFromAt(e.target.value)}
                 className="input-dark h-9 text-sm" data-testid="audit-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-[#8a8a8a] block mb-1">
            {lang === "ar" ? "إلى تاريخ" : "To"}
          </label>
          <input type="date" value={toAt} onChange={(e) => setToAt(e.target.value)}
                 className="input-dark h-9 text-sm" data-testid="audit-to" />
        </div>
        <label className="sm:col-span-2 lg:col-span-5 inline-flex items-center gap-2 text-xs text-[#525252]">
          <input type="checkbox" checked={includeArchived}
                 onChange={(e) => setIncludeArchived(e.target.checked)}
                 className="w-4 h-4 accent-[#c9a55c]" data-testid="audit-include-archived" />
          {lang === "ar" ? "إظهار السجلات المؤرشفة" : "Include archived entries"}
        </label>
      </div>

      {err && (
        <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50">{String(err)}</div>
      )}

      <div className="surface rounded-2xl overflow-hidden">
        <div className="px-4 py-2 text-[11px] uppercase tracking-widest text-[#8a6f2e] border-b border-[#0a0a0a]/[0.05]">
          {lang === "ar" ? `عدد السجلات: ${total}` : `Records: ${total}`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a]/[0.03] text-[#3a3a3a]">
              <tr className="text-[11px] uppercase tracking-widest">
                <th className="text-start px-4 py-3">{lang === "ar" ? "التاريخ والوقت" : "Timestamp"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "المسؤول" : "Actor"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الحساب" : "Target"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الإجراء" : "Action"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "نوع التوثيق" : "Type"}</th>
              </tr>
            </thead>
            <tbody>
              {busy && items.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-[#6b6b6b]" data-testid="audit-empty">
                  {lang === "ar" ? "لا توجد سجلات مطابقة." : "No matching records."}
                </td></tr>
              ) : items.map((row) => (
                <tr key={row.id} className="border-t border-[#0a0a0a]/[0.05]" data-testid={`audit-row-${row.id}`}>
                  <td className="px-4 py-3 text-[#525252]" dir="ltr">{fmt(row.at, lang)}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0a0a0a] inline-flex items-center gap-1">
                      <User className="w-3 h-3 text-[#8a6f2e]" />
                      {row.actor_admin_name || row.actor_admin_email || (lang === "ar" ? "نظام" : "system")}
                    </div>
                    {row.actor_admin_email && (
                      <div className="text-[10px] text-[#8a8a8a]" dir="ltr">{row.actor_admin_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0a0a0a]">
                      {row.target_user_name || row.target_user_email || row.target_user_id?.slice(0, 8) || "—"}
                    </div>
                    {row.target_user_email && (
                      <div className="text-[10px] text-[#8a8a8a]" dir="ltr">{row.target_user_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.kind === "verified" ? (
                      <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#0a4f3a]/10 text-[#0a4f3a] border border-[#0a4f3a]/30">
                        {lang === "ar" ? "تم التوثيق" : "Verified"}
                      </span>
                    ) : (
                      <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300">
                        {lang === "ar" ? "أُلغي التوثيق" : "Unverified"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#525252]">{row.verification_type || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Example sentence for AR — required by the spec */}
      <p className="text-[11px] text-[#8a8a8a] text-center">
        {lang === "ar"
          ? "مثال: قام [اسم المسؤول] بتوثيق الحساب [A] بتاريخ [التاريخ] والوقت [الوقت]."
          : "Example: [Admin] verified account [A] on [Date] at [Time]."}
      </p>
    </div>
  );
}
